/**
 * Map Style Switcher
 *
 * This module provides functionality to switch between different map styles
 * using the style switcher UI, based on the Mapbox example:
 * https://docs.mapbox.com/mapbox-gl-js/example/setstyle/
 */

import { getMapManager } from '../components/Map/MapManager.js';

/**
 * Initialize the map style switcher
 */
export function initMapStyleSwitcher() {
    console.log('[MapStyleSwitcher] Function called');
    
    // Wait for DOM to be fully loaded
    setTimeout(() => {
        const mapManager = getMapManager();
        if (!mapManager || !mapManager.getMap()) {
            console.error('[MapStyleSwitcher] Map not initialized');
            return;
        }
        
        // Get the tools element and info container
        const toolsElement = document.getElementById('tools');
        const infoContainer = document.querySelector('.info-container');
        
        if (!toolsElement) {
            console.error('[MapStyleSwitcher] Tools element not found');
            return;
        }
        
        // Set up a MutationObserver to watch for the info container becoming visible
        if (infoContainer) {
            console.log('[MapStyleSwitcher] Setting up observer for info container');
            
            // Show tools when info container is visible
            const showTools = () => {
                if (infoContainer.classList.contains('visible')) {
                    console.log('[MapStyleSwitcher] Info container is visible, showing tools');
                    toolsElement.style.opacity = '1';
                    toolsElement.style.pointerEvents = 'auto';
                } else {
                    console.log('[MapStyleSwitcher] Info container is not visible, hiding tools');
                    toolsElement.style.opacity = '0';
                    toolsElement.style.pointerEvents = 'none';
                }
            };
            
            // Check initial state
            showTools();
            
            // Set up observer
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.attributeName === 'class') {
                        showTools();
                    }
                });
            });
            
            observer.observe(infoContainer, { attributes: true });
        }
        
        const map = mapManager.getMap();
        const styleOptions = document.querySelectorAll('.style-option');
        
        console.log('[MapStyleSwitcher] Found style options:', styleOptions.length);
        console.log('[MapStyleSwitcher] Style options:', Array.from(styleOptions).map(el => ({
            className: el.className,
            style: el.dataset.style
        })));
        
        if (!styleOptions.length) {
            console.warn('[MapStyleSwitcher] No style options found');
            return;
        }
        
        // Add direct onclick handlers in addition to event listeners
        styleOptions.forEach((option, index) => {
            const style = option.dataset.style;
            console.log(`[MapStyleSwitcher] Setting up option ${index}:`, {
                element: option,
                style: style
            });
            
            // Add direct onclick handler
            option.onclick = function() {
                console.log(`[MapStyleSwitcher] Direct onclick handler for option ${index} with style:`, style);
                handleStyleChange(map, style, styleOptions, option);
            };
        });
        
        // Also add direct click handlers using vanilla JS
        document.getElementById('tools').addEventListener('click', function(e) {
            // Find the closest style-option parent
            const option = e.target.closest('.style-option');
            if (option) {
                const style = option.dataset.style;
                console.log('[MapStyleSwitcher] Clicked via delegation on:', option.className);
                if (style) {
                    handleStyleChange(map, style, styleOptions, option);
                }
            }
        });
        
        console.log('[MapStyleSwitcher] Initializing with', styleOptions.length, 'style options');
    }, 1000); // Wait 1 second for everything to load
}

/**
 * Handle style change
 * @param {Object} map - The Mapbox map instance
 * @param {string} style - The style URL to apply
 * @param {NodeList} styleOptions - All style option elements
 * @param {Element} selectedOption - The selected style option element
 */
function handleStyleChange(map, style, styleOptions, selectedOption) {
    if (!style) {
        console.error('[MapStyleSwitcher] No style defined for option:', selectedOption);
        return;
    }
    
    // Update active state in UI
    styleOptions.forEach(opt => opt.classList.remove('active'));
    selectedOption.classList.add('active');
    
    // Get the map manager
    const mapManager = getMapManager();
    if (!mapManager) {
        console.error('[MapStyleSwitcher] Map manager not available');
        return;
    }
    
    console.log('[MapStyleSwitcher] Using preserveMarkersAcrossStyleChange method');
    
    mapManager.preserveMarkersAcrossStyleChange(style);
}